import { AxiosInstance, CancelToken } from 'axios';

import * as common from '../../../../../../typings/api/skymap/rest/v1/.common';
import * as types from '../../../../../../typings/api/skymap/rest/v1/project';
import { isDefined } from '../../../../utils/variables';
import { toUrlSearchParams } from './utils';

export class ProjectService implements types.Handlers {
  constructor(private axios: AxiosInstance) {}

  async getProjects({ query }: types.GetProjectsParameters): Promise<types.GetProjectsResponse> {
    const { data } = await this.axios.get<types.GetProjectsResponse>('/v1/projects', {
      params: new URLSearchParams([
        ...toUrlSearchParams('include', query.include),
        ...(query.limit !== undefined ? [['limit', String(query.limit)]] : []),
        ...(query.offset !== undefined ? [['offset', String(query.offset)]] : []),
        ...(query.orderBy ? [['orderBy', query.orderBy]] : []),
        ...toUrlSearchParams('lifecycleStatus', query.lifecycleStatus),
        ...(query.name ? [['name', query.name]] : []),
        ...toUrlSearchParams('companyIds', query.companyIds),
      ]),
    });
    return data;
  }

  async getProject({ path, query }: types.GetProjectParameters): Promise<types.GetProjectResponse> {
    const { data } = await this.axios.get<types.GetProjectResponse>(
      `/v1/projects/${path.projectId}`,
      {
        params: new URLSearchParams([...toUrlSearchParams('include', query.include)]),
      },
    );

    return data;
  }

  async getProjectUserGroups(
    params: types.GetProjectUserGroupsParameters,
  ): Promise<common.GetProjectUserGroupsResponse> {
    const { data } = await this.axios.get<common.GetProjectUserGroupsResponse>(
      `/v1/projects/${params.path.projectId}/user-groups`,
      {
        params: new URLSearchParams([
          ...toUrlSearchParams('include', params.query.include as string[]),
        ]),
      },
    );
    return data;
  }

  async getProjectUsers(
    params: types.GetProjectUsersParameters,
  ): Promise<common.GetProjectUsersResponse> {
    const { data } = await this.axios.get<common.GetProjectUsersResponse>(
      `/v1/projects/${params.path.projectId}/users`,
      {
        params: new URLSearchParams([
          ...toUrlSearchParams('include', params.query.include as string[]),
        ]),
      },
    );
    return data;
  }

  async removeDroneHarmonyIntegration(
    params: types.RemoveDroneHarmonyIntegrationParameters,
  ): Promise<void> {
    await this.axios.delete<void>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony`,
    );
  }

  async createDroneHarmonyFlightPlan(
    params: types.CreateDroneHarmonyFlightPlanParameters,
  ): Promise<types.CreateDroneHarmonyFlightPlanResponse> {
    const { data } = await this.axios.post<types.CreateDroneHarmonyFlightPlanResponse>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony/flight-plans`,
      params.body,
    );
    return data;
  }

  async patchDroneHarmonyFlightPlan(
    params: types.PatchDroneHarmonyFlightPlanParameters,
  ): Promise<types.CreateDroneHarmonyFlightPlanResponse> {
    const { data } = await this.axios.patch<types.CreateDroneHarmonyFlightPlanResponse>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony/flight-plans/${params.path.flightPlanId}`,
      params.body,
    );
    return data;
  }

  async fetchDroneHarmonyFlightPlans(
    params: types.FetchDroneHarmonyFlightPlansParameters,
  ): Promise<types.FetchDroneHarmonyFlightPlanResponse> {
    const { data } = await this.axios.get<types.FetchDroneHarmonyFlightPlanResponse>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony/flight-plans`,
    );
    return data;
  }

  async deleteDroneHarmonyFlightPlan(
    params: types.DeleteDroneHarmonyFlightPlanParameters,
  ): Promise<void> {
    await this.axios.delete<void>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony/flight-plans/${params.path.flightPlanId}`,
    );
  }

  onDroneHarmonyNotification(_params: types.OnDroneHarmonyNotificationParameters): Promise<void> {
    throw new Error('Method not implemented.');
  }

  async getDroneHarmonyIntegration(
    params: types.GetDroneHarmonyIntegrationParameters,
  ): Promise<types.DroneHarmonyIntegrationResponse> {
    const { data } = await this.axios.get<types.DroneHarmonyIntegrationResponse>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony`,
    );
    return data;
  }

  async createDroneHarmonyIntegration(
    params: types.CreateDroneHarmonyIntegrationParameters,
  ): Promise<types.DroneHarmonyIntegrationResponse> {
    const { data } = await this.axios.post<types.DroneHarmonyIntegrationResponse>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony`,
    );
    return data;
  }

  async deleteAllAttachedDongles(params: types.DeleteAllAttachedDonglesParameters): Promise<void> {
    const projectId: string = params.path.projectId;
    await this.axios.delete(`v1/projects/${projectId}/dongles`);
  }

  async getAttachedDonglesList(
    params: types.GetAttachedDonglesListParameters,
  ): Promise<common.FetchSkyMapConnectDongleRegistrationsResponse> {
    const projectId: string = params.path.projectId;
    const { data } = await this.axios.get<common.FetchSkyMapConnectDongleRegistrationsResponse>(
      `v1/projects/${projectId}/dongles`,
    );
    return data;
  }

  async getArchiveJobs(
    params: types.GetArchiveJobsParameters,
  ): Promise<types.GetProjectArchiveJobsResponse> {
    const { data } = await this.axios.get<types.GetProjectArchiveJobsResponse>(
      `/v1/projects/${params.path.projectId}/archive-jobs`,
      {
        params: new URLSearchParams([
          ...toUrlSearchParams('include', params.query.include),
          ...toUrlSearchParams('orderBy', params.query.orderBy),
        ]),
      },
    );
    return data;
  }
  async getRestoreJobs(
    params: types.GetRestoreJobsParameters,
  ): Promise<types.GetProjectRestoreJobsResponse> {
    const { data } = await this.axios.get<types.GetProjectRestoreJobsResponse>(
      `/v1/projects/${params.path.projectId}/restore-jobs`,
      {
        params: new URLSearchParams([
          ...toUrlSearchParams('include', params.query.include),
          ...toUrlSearchParams('orderBy', params.query.orderBy),
        ]),
      },
    );
    return data;
  }

  async addArchiveJob(params: types.AddArchiveJobParameters): Promise<void> {
    await this.axios.post<common.FolderModelResponse>(
      `/v1/projects/${params.path.projectId}/archive-jobs`,
      params.body,
    );
  }

  async addRestoreJob(params: types.AddRestoreJobParameters): Promise<void> {
    await this.axios.post<common.FolderModelResponse>(
      `/v1/projects/${params.path.projectId}/restore-jobs`,
    );
  }

  async createSkyviewExportJob(params: types.CreateSkyviewExportJobParameters): Promise<void> {
    await this.axios.post<void>(
      `/v1/projects/${params.path.projectId}/skyview-export-job`,
      params.body,
    );
  }

  async deleteArchiveJob(params: types.DeleteArchiveJobParameters): Promise<void> {
    await this.axios.delete<common.FolderModelResponse>(
      `/v1/projects/${params.path.projectId}/archive-jobs/${params.path.archiveJobId}`,
    );
  }

  async patchProject(params: types.PatchProjectParameters): Promise<void> {
    await this.axios.patch<common.FileModelResponse>(
      `/v1/projects/${params.path.projectId}`,
      params.body,
    );
  }

  async getRootFolders(
    params: types.GetRootFoldersParameters,
    cancelToken?: CancelToken,
  ): Promise<common.GetFoldersResponse> {
    const { data } = await this.axios.get<common.GetFoldersResponse>(
      `/v1/projects/${params.path.projectId}/folders`,
      {
        params: new URLSearchParams([
          ...(params.query.includeSubFolders ? [['includeSubFolders', 'true']] : []),
          ...(params.query.include ?? []).map((x) => ['include', x]),
          ...(params.query.orderBy ?? []).map((x) => ['orderBy', x]),
        ]),
        cancelToken: cancelToken,
      },
    );
    return data;
  }

  async createRootFolder(
    params: types.CreateRootFolderParameters,
  ): Promise<common.FolderModelResponse> {
    const { data } = await this.axios.post<common.FolderModelResponse>(
      `/v1/projects/${params.path.projectId}/folders`,
      params.body,
    );
    return data;
  }

  async createRootFolderFile(
    params: types.CreateRootFolderFileParameters,
  ): Promise<common.CreateFileResponse> {
    const { data } = await this.axios.post<common.CreateFileResponse>(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `/v1/projects/${params.path.projectId}/files`,
      params.body,
    );
    return data;
  }

  async getRootFolderFiles(
    params: types.GetRootFolderFilesParameters,
    cancelToken?: CancelToken,
  ): Promise<common.GetFilesResponse> {
    const { data } = await this.axios.get<common.GetFilesResponse>(
      `/v1/projects/${params.path.projectId}/files`,
      {
        params: new URLSearchParams([
          ...(params.query.include ?? []).map((x) => ['include', x]),
          ...(params.query.orderBy ?? []).map((x) => ['orderBy', x]),
        ]),
        cancelToken: cancelToken,
      },
    );
    return data;
  }

  async getTiledModels(
    params: types.GetTiledModelsParameters,
  ): Promise<common.GetTiledModelListResponse> {
    const { data } = await this.axios.get<common.GetTiledModelListResponse>(
      `/v1/projects/${params.path.projectId}/tiled-models`,
    );
    return data;
  }

  async getTimelineContainers(
    params: types.GetTimelineContainersParameters,
  ): Promise<types.GetTimelineContainersResponse> {
    const { data } = await this.axios.get<types.GetTimelineContainersResponse>(
      `/v1/projects/${params.path.projectId}/timelines`,
      {
        params: new URLSearchParams([...(params.query.include ?? []).map((x) => ['include', x])]),
      },
    );
    return data;
  }

  async getCoordinateSystem(
    params: types.GetCoordinateSystemParameters,
  ): Promise<common.CoordinateSystemResponse> {
    const { data } = await this.axios.get<common.CoordinateSystemResponse>(
      `/v1/projects/${params.path.projectId}/coordinate-system`,
    );
    return data;
  }

  async putCoordinateSystem(
    params: types.PutCoordinateSystemParameters,
  ): Promise<common.CoordinateSystemResponse> {
    const { data } = await this.axios.put<common.CoordinateSystemResponse>(
      `/v1/projects/${params.path.projectId}/coordinate-system`,
      params.body,
    );
    return data;
  }

  async getGeoid(params: types.GetGeoidParameters): Promise<common.GetGeoidResponse> {
    const { data } = await this.axios.get<common.GetGeoidResponse>(
      `/v1/projects/${params.path.projectId}/geoid`,
    );
    return data;
  }

  async getPois(params: types.GetPoisParameters): Promise<types.GetProjectPoiResponse> {
    const {
      createdAtFrom,
      createdAtTo,
      include,
      createdById,
      tagGroups,
      imageCapturedAtFrom,
      imageCapturedAtTo,
      timelineContainerId,
    } = params.query;
    const { data } = await this.axios.get<types.GetProjectPoiResponse>(
      `/v1/projects/${params.path.projectId}/poi`,
      {
        params: new URLSearchParams([
          ...(include ?? []).map((x) => ['include', x]),
          ...(createdAtFrom ? [['createdAtFrom', createdAtFrom]] : []),
          ...(createdAtTo ? [['createdAtTo', createdAtTo]] : []),
          ...toUrlSearchParams('createdById', createdById),
          ...toUrlSearchParams('tagGroups', tagGroups),
          ...(imageCapturedAtFrom ? [['imageCapturedAtFrom', imageCapturedAtFrom]] : []),
          ...(imageCapturedAtTo ? [['imageCapturedAtTo', imageCapturedAtTo]] : []),
          ...(isDefined(timelineContainerId)
            ? [['timelineContainerId', String(timelineContainerId)]]
            : []),
        ]),
      },
    );
    return data;
  }

  async getDynamicAssets(
    params: types.GetDynamicAssetsParameters,
  ): Promise<types.GetDynamicAssetsResponse> {
    const { createdAtFrom, createdAtTo, type, createdById, tagGroups, timelineContainerId } =
      params.query;
    const { data } = await this.axios.get<types.GetDynamicAssetsResponse>(
      `/v1/projects/${params.path.projectId}/assets`,
      {
        params: new URLSearchParams([
          ...(createdAtFrom ? [['createdAtFrom', createdAtFrom]] : []),
          ...(createdAtTo ? [['createdAtTo', createdAtTo]] : []),
          ...(isDefined(type) ? toUrlSearchParams('type', type as string[]) : []),
          ...toUrlSearchParams('createdById', createdById),
          ...toUrlSearchParams('tagGroups', tagGroups),
          ...(isDefined(timelineContainerId)
            ? [['timelineContainerId', String(timelineContainerId)]]
            : []),
        ]),
      },
    );
    return data;
  }

  async getTerrainModels(
    params: types.GetTerrainModelsParameters,
  ): Promise<types.GetTerrainModelsResponse> {
    const { createdAtFrom, createdAtTo, timelineContainerId } = params.query;
    const { data } = await this.axios.get<types.GetTerrainModelsResponse>(
      `/v1/projects/${params.path.projectId}/terrain-models`,
      {
        params: new URLSearchParams([
          ...(createdAtFrom ? [['createdAtFrom', createdAtFrom]] : []),
          ...(createdAtTo ? [['createdAtTo', createdAtTo]] : []),
          ...(isDefined(timelineContainerId)
            ? [['timelineContainerId', String(timelineContainerId)]]
            : []),
        ]),
      },
    );
    return data;
  }

  async createAssetTag(
    params: types.CreateAssetTagParameters,
  ): Promise<types.CreateAssetTagResponse> {
    const { data } = await this.axios.post<types.CreateAssetTagResponse>(
      `/v1/projects/${params.path.projectId}/asset-tags`,
      params.body,
    );
    return data;
  }

  async getAssetTags(params: types.GetAssetTagsParameters): Promise<types.GetAssetTagsResponse> {
    const { data } = await this.axios.get<types.GetAssetTagsResponse>(
      `/v1/projects/${params.path.projectId}/asset-tags`,
    );
    return data;
  }

  async createPoi(params: types.CreatePoiParameters): Promise<types.CreateProjectPoiResponse> {
    const { data } = await this.axios.post<types.CreateProjectPoiResponse>(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `/v1/projects/${params.path.projectId}/poi`,
      params.body,
      {
        params: new URLSearchParams([...(params.query.include ?? []).map((x) => ['include', x])]),
      },
    );
    return data;
  }

  async createImage(
    params: types.CreateImageParameters,
  ): Promise<types.CreateImageRequestResponse> {
    const { data } = await this.axios.post<types.CreateImageRequestResponse>(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `/v1/projects/${params.path.projectId}/images`,
      params.body,
    );
    return data;
  }

  async putGeoid(params: types.PutGeoidParameters): Promise<common.GetGeoidOptionalResponse> {
    const { data } = await this.axios.put<common.GetGeoidOptionalResponse>(
      `/v1/projects/${params.path.projectId}/geoid`,
      params.body,
    );
    return data;
  }

  async getRealProperties(
    params: types.GetRealPropertiesParameters,
  ): Promise<types.GetRealPropertiesResponse> {
    const { data } = await this.axios.get<types.GetRealPropertiesResponse>(
      `/v1/projects/real-properties`,
      {
        params: params.query,
      },
    );
    return data;
  }

  async getProjectRealProperties(
    params: types.GetProjectRealPropertiesParameters,
  ): Promise<types.GetProjectRealPropertiesResponse> {
    const { data } = await this.axios.get<types.GetProjectRealPropertiesResponse>(
      `/v1/projects/${params.path.projectId}/real-properties`,
    );
    return data;
  }

  async createProjectRealProperty(
    params: types.CreateProjectRealPropertyParameters,
  ): Promise<types.CreateProjectRealPropertyResponse> {
    const { data } = await this.axios.post<types.CreateProjectRealPropertyResponse>(
      `/v1/projects/${params.path.projectId}/real-properties/${params.path.externalId}`,
    );
    return data;
  }

  async getProjectGeodata(
    params: types.GetProjectGeodataParameters,
  ): Promise<types.FetchProjectGeodataListResponse> {
    const { data } = await this.axios.get<types.FetchProjectGeodataListResponse>(
      `/v1/projects/${params.path.projectId}/geodata`,
      {
        params: new URLSearchParams([...toUrlSearchParams('include', params.query.include)]),
        headers: {
          ...params.headers,
        },
      },
    );
    return data;
  }
}
